import axios from "axios";
import fetchJsonp from "fetch-jsonp";
import React from "react";
import Format from "string-format";
import { parseISO as parseISODate } from "date-fns";
import { t } from "i18next";
import { Box, Divider, Flex, Text } from "@chakra-ui/react";
import { getHeightAndWidth, getMediaDurationFromFile } from "../_utils/common";
import { TypeOfDesignToBePublished } from "../_utils/constants";
import SocialAccountAvatar from "../_components/social/SocialAccountAvatar/SocialAccountAvatar";
import PostStatusIcon from "../_components/social/PostStatusIcon/PostStatusIcon";
import { PUBLISHED, PUBLISH_ERROR } from "../_constants/PostStatus";
import {
  ASSETS_ENDPOINT,
  CHECK_TASK_STATAUS,
  CONNECTED_APPS,
  DELETE_ASSET_ENDPOINT,
  GET_S3_SIGNED_URL_ENDPOINT,
  PLANNER_ALL_CHANNELS_ENDPOINT,
  PLANNER_ALL_SLOTS_ENDPOINT,
  PLANNER_SLOTS_PER_CHANNEL_ENDPOINT,
  PLANNER_SLOT_ENDPOINT,
  PLANNER_GET_POSTS_CALENDAR_ENDPOINT,
  PLANNER_GET_POSTS_LIST_ENDPOINT,
  PLANNER_GET_POST_DETAILS_ENDPOINT,
  PLANNER_MOVE_POSTS_ENDPOINT,
  PLANNER_SWAP_POSTS_ENDPOINT,
  PLANNER_DELETE_POST_ENDPOINT,
  PLANNER_MULTIPLY_POST_ENDPOINT,
  PLANNER_MENTIONS_FETCH_ENDPOINT,
  PLANNER_TWITTER_MENTIONS_FETCH_ENDPOINT,
  PLANNER_SAVE_POST_ENDPOINT,
  PLANNER_ADD_SCHEDULE_POST_ENDPOINT,
  PLANNER_ADD_BUFFER_POST_ENDPOINT,
  PLANNER_SAVE_DRAFT_ENDPOINT,
  PLANNER_ADD_DRAFT_ENDPOINT,
  PLANNER_ADD_FOR_APPROVAL_DRAFT_ENDPOINT,
  PLANNER_GET_SNIPPETS_ENDPOINT,
  PLANNER_ADD_SNIPPET_ENDPOINT,
  PLANNER_REMOVE_SNIPPET_ENDPOINT,
  PLANNER_EDIT_SNIPPET_ENDPOINT,
  PLANNER_USED_SNIPPET_ENDPOINT,
  PLANNER_MULTIPART_UPLOAD_ENDPOINT,
  PLANNER_UPDATE_WORKFLOW_TYPE_ENDPOINT,
  PLANNER_GET_DRAFTS_ENDPOINT,
  PLANNER_GET_DRAFT_ENDPOINT,
  PLANNER_GET_DRAFT_HISTORY_ENDPOINT,
  PLANNER_DELETE_DRAFT_ENDPOINT,
  DISCONNECT_PARTNER_ENDPOINT,
  PLANNER_GET_AVAILABLE_POSTS_QUOTA,
  PLANNER_GET_EVENTS_ENDPOINT,
  PLANNER_GET_EVENTS_CATEGORIES_ENDPOINT,
  PLANNER_ADD_ASYNC_PUBLISH_NOW_POST_ENDPOINT,
  PLANNER_ASYNC_PUBLISH_NOW_POST_ENDPOINT,
  PLANNER_ADD_ASYNC_STATUS_POST_ENDPOINT,
  PLANNER_SET_DRAFT_STATUS,
  PLANNER_GET_APPROVAL_PACKAGE,
  PLANNER_GET_APPROVAL_PACKAGES,
  PLANNER_CREATE_APPROVAL_PACKAGE,
  PLANNER_UPDATE_APPROVAL_PACKAGE,
  PLANNER_APPROVAL_PACKAGE_APPROVE,
  PLANNER_APPROVAL_PACKAGE_REJECT,
  SOCIAL_ACCOUNTS_GET_ADDED,
  PLANNER_MULTIPLY_REMOVE_COPIES_ENDPOINT,
  PLANNER_MULTIPLY_LIST_ENDPOINT,
  PLANNER_GENERATE_REPORT_ENDPOINT,
} from "./endpoints";
import { handleHTTPError } from "./errorHandlerActions";
import { uploadToS3 } from "./storiesActions";
import {
  GET_PARTNERS,
  ADD_PARTNER,
  DELETE_PARTNER,
  DELETE_POST,
  SET_CALENDAR_DATE,
  SET_DESIGN,
  REMOVE_DESIGN,
  SET_CHANNEL_SLOTS,
  UPDATE_CHANNEL_SLOTS,
  DELETE_CHANNEL_SLOT,
  REFRESH_POST_VIEW,
  SET_POSTS_VIEW,
  SET_POSTS,
  SET_POSTS_LIST,
  START_MOVE_POSTS,
  END_MOVE_POSTS,
  START_SWAP_POSTS,
  END_SWAP_POSTS,
  SET_FULL_SCREEN,
  OPEN_POST_EDITOR,
  CLOSE_POST_EDITOR,
  SET_MEDIA_SIDEBAR_PANEL_OPEN,
  SET_PLANNER_VIEW,
  SET_PLANNER_TIMEZONE,
  SET_DRAFTS_VIEW,
  SET_DRAFTS_LIST,
  REFRESH_DRAFTS_VIEW,
  SET_WORKFLOW_TYPE,
  SET_DRAFTS_DATA,
  DELETE_DRAFT,
  SET_POSTS_BULK_IMPORT_OPEN,
  SET_POSTS_BULK_IMPORT_ITEMS,
  UPDATE_POSTS_BULK_IMPORT_ITEM,
  REMOVE_POSTS_BULK_IMPORT_ITEM,
  SET_EVENTS_BY_DAY,
  SET_EVENTS_CATEGORIES,
  SET_SELECTED_EVENTS_CATEGORIES,
} from "./types";
import * as PostEditorTabsTypes from "../_components/social/PostEditor/PostEditorTabsTypes.constants";
import { showToast } from "../_utils/showToast";

export const handlePlannerResponse = (response, dispatch) => {
  /* temporarly solution to handle toasters from Postfity's specific response structure 
    (status === 1 for success, message in results array, etc);
  */
  const messages = [];
  if (response?.data?.results && Array.isArray(response?.data.results)) {
    response.data.results
      .sort((a, b) => (a.status < b.status ? 1 : -1))
      .forEach((item, i) => {
        if (!item.message) {
          return;
        }

        if (item.account?.name) {
          const isLast = i === response.data.results.length - 1;
          const postStatus = item.status === 3 ? PUBLISH_ERROR : PUBLISHED;
          const account = (
            <Box mb={isLast ? "0" : "3"}>
              <Flex>
                <PostStatusIcon
                  mr="2"
                  mt="0.5"
                  status={postStatus}
                  color="inherit"
                />
                <Text overflow="hidden" w="100%" fontSize="sm">
                  {item.message}
                </Text>
              </Flex>
              <Flex mt="2" pl="5">
                <SocialAccountAvatar
                  name={item.account.name}
                  src={item.account.image}
                  socialType={item.account.social_type}
                  socialTypeShown
                  size="xs"
                  mt="1"
                  mr="3"
                />
                <Box>
                  <Text fontSize="xs" lineHeight="1rem" mb="0">
                    {item.account.name}
                  </Text>
                  <Text as="span" fontSize="xs">
                    {t(`social_types.${item.account.social_type}.full`, {
                      ns: "planner",
                    })}
                    {item.account.parent
                      ? ` (${item.account.parent.name})`
                      : ""}
                  </Text>
                </Box>
              </Flex>
              {!isLast && <Divider mb="0" mt="2" />}
            </Box>
          );

          messages.push(account);
          return;
        }

        if (item?.message) {
          messages.push(item.message);
        }
      });

    const items = messages.map((msg) => <Box key={msg}>{msg}</Box>);

    if (response.data.status === 1) {
      if (items.length) {
        showToast({
          description: items,
          title: "Success",
          status: "success",
        });
      }
    } else {
      showToast({
        description: items.length
          ? items
          : t("errors.unknown_error", { ns: "common" }),
        title: t("error", { ns: "common" }),
        status: "error",
        duration: null,
      });
    }
  }
};

export const fetchPartners = (signalToken) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .get(CONNECTED_APPS, {
        cancelToken: signalToken,
      })
      .then((response) => {
        dispatch({
          type: GET_PARTNERS,
          payload: response.data,
        });

        resolve(response);
      })
      .catch((err) => {
        dispatch(handleHTTPError(err));
      });
  });
};

export const addPartner = (partner) => (dispatch) => {
  dispatch({
    type: ADD_PARTNER,
    payload: partner,
  });
};

export const deletePartner = (partnerId) => (dispatch) => {
  dispatch({
    type: DELETE_PARTNER,
    payload: partnerId,
  });
};

export const disconnectPartner = (partnerId) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .delete(Format(DISCONNECT_PARTNER_ENDPOINT, partnerId))
      .then((res) => {
        showToast({
          description: t("disconnect_success", { ns: "common" }),
          title: t("success", { ns: "common" }),
          status: "success",
        });
        resolve(res.data);
      })
      .catch((error) => {
        reject(error);
        dispatch(handleHTTPError(error));
      });
  });
};

export const fetchAllChannels = () => (dispatch) => {
  // @TODO not used
  return new Promise((resolve, reject) => {
    axios
      .get(PLANNER_ALL_CHANNELS_ENDPOINT)
      .then((res) => {
        resolve(res.data.results);
      })
      .catch((error) => {
        dispatch(handleHTTPError(error));
      });
  });
};

export const setDesignToPublish = (design, type) => (dispatch) => {
  dispatch({
    type: SET_DESIGN,
    payload: {
      designData: design,
      type: type,
    },
  });
};

export const removeDesignToPublish = (id) => (dispatch) => {
  dispatch({
    type: REMOVE_DESIGN,
    payload: id,
  });
};

export const uploadUserAssetForPublishing =
  (imageFile, signal) => (dispatch) => {
    // @TODO not used
    return new Promise((resolve, reject) => {
      let objectSize = {};
      getHeightAndWidth(imageFile)
        .then((size) => {
          objectSize = size;
          return axios.post(
            GET_S3_SIGNED_URL_ENDPOINT,
            {
              filename: imageFile.name,
              filetype: imageFile.type,
              action: "assets",
            },
            {
              cancelToken: signal.token,
            }
          );
        })
        .then((response) => {
          return uploadToS3(response, imageFile);
        })
        .then((response) => {
          const request = response.data;
          request["thumbnail"] = response.data.asset;
          request["thumbnail_height"] = objectSize.height;
          request["thumbnail_width"] = objectSize.width;
          return axios.post(ASSETS_ENDPOINT, request);
        })
        .then((response) => {
          const url = response.data.thumbnail;
          const id = response.data.id;
          const imageData = {
            source: url,
            id: id,
            thumbnail: response.data.thumbnail_cover_image,
          };
          dispatch(
            setDesignToPublish(imageData, TypeOfDesignToBePublished.ASSET)
          );

          resolve(response);
        })
        .catch((error) => {
          dispatch(handleHTTPError(error));
        });
    });
  };

export const deleteUserAssetForPublishing = (id, signalToken) => (dispatch) => {
  // @TODO not used
  axios
    .delete(Format(DELETE_ASSET_ENDPOINT, id), {
      cancelToken: signalToken,
    })
    .then((res) => {
      dispatch(removeDesignToPublish(id));
    })
    .catch((error) => {
      dispatch(handleHTTPError(error));
    });
};

export const fetchSlotsSequenceForChannel = (channelId) => (dispatch) => {
  // @TODO not used
  return new Promise((resolve, reject) => {
    axios
      .get(Format(`${PLANNER_ALL_SLOTS_ENDPOINT}?size=3`, channelId))
      .then((res) => {
        var slots = res.data.length > 0 ? res.data : [];
        slots = slots.map((slot, index) => {
          return slot.replace("Z", "");
        });

        resolve(slots);
      })
      .catch((error) => {
        dispatch(handleHTTPError(error));
      });
  });
};

export const fetchSlotsForChannel = (channelId) => (dispatch) => {
  // @TODO not used
  return new Promise((resolve, reject) => {
    axios
      .get(Format(PLANNER_SLOTS_PER_CHANNEL_ENDPOINT, channelId))
      .then((res) => {
        dispatch({
          type: SET_CHANNEL_SLOTS,
          payload: res.data.results,
        });

        resolve(res);
      })
      .catch((error) => {
        dispatch(handleHTTPError(error));
      });
  });
};

export const createSlotForChannel = (channelId, slotBody) => (dispatch) => {
  // @TODO not used
  return new Promise((resolve, reject) => {
    axios
      .post(Format(PLANNER_SLOTS_PER_CHANNEL_ENDPOINT, channelId), slotBody)
      .then((res) => {
        showToast({
          description: t("slot.added", { ns: "planner" }),
          title: t("success", { ns: "common" }),
          status: "success",
        });

        dispatch({
          type: UPDATE_CHANNEL_SLOTS,
          payload: res.data,
        });

        resolve(res);
      })
      .catch((error) => {
        dispatch(handleHTTPError(error));
      });
  });
};

export const deleteSlotForChannel = (channelId, slotId) => (dispatch) => {
  // @TODO not used
  return new Promise((resolve, reject) => {
    axios
      .delete(Format(PLANNER_SLOT_ENDPOINT, channelId, slotId))
      .then((res) => {
        showToast({
          description: t("slot.deleted", { ns: "planner" }),
          title: t("success", { ns: "common" }),
          status: "success",
        });

        dispatch({
          type: DELETE_CHANNEL_SLOT,
          payload: slotId,
        });

        resolve(res);
      })
      .catch((error) => {
        dispatch(handleHTTPError(error));
      });
  });
};

export const openPostEditor = (payload) => (dispatch) => {
  dispatch({
    type: CLOSE_POST_EDITOR,
  });

  setTimeout(() => {
    dispatch({
      type: OPEN_POST_EDITOR,
      payload,
    });
  });
};

export const closePostEditor = () => (dispatch) => {
  dispatch({
    type: CLOSE_POST_EDITOR,
  });
};

export const setMediaSidebarPanelOpen = (value) => (dispatch) => {
  dispatch({
    type: SET_MEDIA_SIDEBAR_PANEL_OPEN,
    payload: value,
  });
};

export const refreshPostsView = () => (dispatch) => {
  dispatch({
    type: REFRESH_POST_VIEW,
  });
};

export const setPublishingView = (payload) => (dispatch) => {
  dispatch({
    type: SET_PLANNER_VIEW,
    payload,
  });
};

export const setCalendarDate = (payload) => (dispatch) => {
  dispatch({
    type: SET_CALENDAR_DATE,
    payload,
  });
};

export const setPlannerTimezone = (payload) => (dispatch) => {
  dispatch({
    type: SET_PLANNER_TIMEZONE,
    payload,
  });
};

export const setPostsView = (payload) => (dispatch) => {
  dispatch({
    type: SET_POSTS_VIEW,
    payload,
  });
};

export const setPosts = (payload) => (dispatch) => {
  dispatch({
    type: SET_POSTS,
    payload,
  });
};

export const setPostsList = (payload) => (dispatch) => {
  dispatch({
    type: SET_POSTS_LIST,
    payload,
  });
};

export const fetchEventsCategories = () => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .get(PLANNER_GET_EVENTS_CATEGORIES_ENDPOINT)
      .then((res) => {
        dispatch(setEventsCategories(res.data?.items));
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const fetchEvents =
  ({ beginDate, endDate, categories }) =>
  (dispatch) => {
    const params = {
      beginDate,
      endDate,
      cat: categories,
    };

    return new Promise((resolve, reject) => {
      axios
        .get(PLANNER_GET_EVENTS_ENDPOINT, { params })
        .then((res) => {
          resolve(res);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

export const fetchPostsCalendar =
  ({ beginDate, endDate, timezone, accounts }) =>
  (dispatch) => {
    return new Promise((resolve, reject) => {
      axios
        .post(PLANNER_GET_POSTS_CALENDAR_ENDPOINT, {
          beginDate,
          endDate,
          tz: timezone,
          a: accounts,
        })
        .then((res) => {
          if (res.data.status !== 1) {
            handlePlannerResponse(res, dispatch);
          }

          resolve(res);
        })
        .catch((error) => {
          handlePlannerResponse(error.response, dispatch);
          dispatch(handleHTTPError(error));
          reject(error);
        });
    });
  };

export const fetchPostsList =
  ({ page, accounts, perPage, category, timezone, search }) =>
  (dispatch) => {
    const query = new Date().getTime();
    const url = Format(
      PLANNER_GET_POSTS_LIST_ENDPOINT,
      page,
      accounts,
      perPage,
      category,
      timezone,
      search,
      query
    );

    return new Promise((resolve, reject) => {
      axios
        .get(url)
        .then((res) => {
          if (res.data.status !== 1) {
            handlePlannerResponse(res, dispatch);
          }

          resolve(res);
        })
        .catch((error) => {
          handlePlannerResponse(error.response, dispatch);
          dispatch(handleHTTPError(error));
          reject(error);
        });
    });
  };

export const movePostsTime =
  ({ id, date, time }) =>
  (dispatch) => {
    dispatch({
      type: START_MOVE_POSTS,
      payload: {
        id,
        date: parseISODate(`${date} ${time}`),
      },
    });

    return new Promise((resolve, reject) => {
      axios
        .post(PLANNER_MOVE_POSTS_ENDPOINT, {
          id,
          date,
          time,
        })
        .then((res) => {
          dispatch({
            type: END_MOVE_POSTS,
            payload: {
              id,
              error: !res.data || res.data?.status !== 1,
            },
          });

          handlePlannerResponse(res, dispatch);
          resolve(res);
        })
        .catch((error) => {
          dispatch({
            type: END_MOVE_POSTS,
            payload: {
              id,
              error: true,
            },
          });
          handlePlannerResponse(error.response, dispatch);
          dispatch(handleHTTPError(error));
          reject(error);
        });
    });
  };

export const swapPostsTime =
  ({ id, secondId }) =>
  (dispatch) => {
    dispatch({
      type: START_SWAP_POSTS,
      payload: {
        id,
        secondId,
      },
    });

    return new Promise((resolve, reject) => {
      axios
        .post(PLANNER_SWAP_POSTS_ENDPOINT, {
          id,
          secondId,
        })
        .then((res) => {
          dispatch({
            type: END_SWAP_POSTS,
            payload: {
              id,
              secondId,
              error: !res.data || res.data?.status !== 1,
            },
          });

          handlePlannerResponse(res, dispatch);
          resolve(res);
        })
        .catch((error) => {
          dispatch({
            type: END_SWAP_POSTS,
            payload: {
              id,
              secondId,
              error: true,
            },
          });
          handlePlannerResponse(error.response, dispatch);
          dispatch(handleHTTPError(error));
          reject(error);
        });
    });
  };

export const deletePost = (postId) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .post(PLANNER_DELETE_POST_ENDPOINT, {
        post_schedule_id: postId,
      })
      .then((res) => {
        dispatch({
          type: DELETE_POST,
          payload: postId,
        });

        handlePlannerResponse(res, dispatch);
        resolve(res);
      })
      .catch((error) => {
        handlePlannerResponse(error.response, dispatch);
        dispatch(handleHTTPError(error));
        reject(error);
      });
  });
};

export const multiplyPost = (data) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .post(PLANNER_MULTIPLY_POST_ENDPOINT, data)
      .then((res) => {
        handlePlannerResponse(res, dispatch);
        resolve(res);
      })
      .catch((error) => {
        handlePlannerResponse(error.response, dispatch);
        dispatch(handleHTTPError(error));
        reject(error);
      });
  });
};

export const multiplyRemoveCopies = ({ postId }) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .post(PLANNER_MULTIPLY_REMOVE_COPIES_ENDPOINT, { postId })
      .then((res) => {
        handlePlannerResponse(res, dispatch);
        resolve(res);
      })
      .catch((error) => {
        handlePlannerResponse(error.response, dispatch);
        dispatch(handleHTTPError(error));
        reject(error);
      });
  });
};

export const fetchMultiplyList =
  ({ page, perPage, category, timezone, postId }) =>
    (dispatch) => {
      const query = new Date().getTime();
      const params = {
        page,
        perPage,
        category,
        timezone,
        postId,
        query
      };

      return new Promise((resolve, reject) => {
        axios
          .get(PLANNER_MULTIPLY_LIST_ENDPOINT, {
            params,
          })
          .then((res) => {
            if (res.data.status !== 1) {
              handlePlannerResponse(res, dispatch);
            }

            resolve(res);
          })
          .catch((error) => {
            handlePlannerResponse(error.response, dispatch);
            dispatch(handleHTTPError(error));
            reject(error);
          });
      });
    };

export const publishPostNow = (postId) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .post(PLANNER_ASYNC_PUBLISH_NOW_POST_ENDPOINT, {
        postId,
      })
      .then((res) => {
        if (res.data.status === 1) {
          const PENDING_STATUS = 100;

          const timer = setInterval(async () => {
            try {
              const statusResponse = await axios.get(
                PLANNER_ADD_ASYNC_STATUS_POST_ENDPOINT,
                {
                  params: {
                    ids: res.data?.results || [],
                  },
                }
              );

              if (statusResponse.data.status !== PENDING_STATUS) {
                clearInterval(timer);
                handlePlannerResponse(statusResponse, dispatch);
                resolve(statusResponse);
              }
            } catch (error) {
              handlePlannerResponse(error.response, dispatch);
              reject(error);
            }
          }, 5000);
        } else {
          handlePlannerResponse(res, dispatch);
          resolve(res);
        }
      })
      .catch((error) => {
        handlePlannerResponse(error.response, dispatch);
        dispatch(handleHTTPError(error));
        reject(error);
      });
  });
};

export const fetchPostDetails = (postId) => {
  const url = Format(PLANNER_GET_POST_DETAILS_ENDPOINT, postId);
  return new Promise((resolve, reject) => {
    axios
      .get(url)
      .then((response) => {
        resolve(response);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export const setFullScreen = (value) => (dispatch) => {
  dispatch({
    type: SET_FULL_SCREEN,
    payload: value,
  });
};

export const setEventsCategories = (items) => (dispatch) => {
  dispatch({
    type: SET_EVENTS_CATEGORIES,
    payload: items,
  });
};

export const setSelectedEventsCategories = (ids) => (dispatch) => {
  dispatch({
    type: SET_SELECTED_EVENTS_CATEGORIES,
    payload: ids,
  });
};

export const setEventsByDay = (items) => (dispatch) => {
  dispatch({
    type: SET_EVENTS_BY_DAY,
    payload: items,
  });
};

export const setPostsBulkImportOpen = (value) => (dispatch) => {
  dispatch({
    type: SET_POSTS_BULK_IMPORT_OPEN,
    payload: value,
  });
};

export const setPostsBulkImportItems = (items) => (dispatch) => {
  dispatch({
    type: SET_POSTS_BULK_IMPORT_ITEMS,
    payload: items,
  });
};

export const updatePostsBulkImportItem = (item) => (dispatch) => {
  dispatch({
    type: UPDATE_POSTS_BULK_IMPORT_ITEM,
    payload: item,
  });
};

export const removePostsBulkImportItem = (itemId) => (dispatch) => {
  dispatch({
    type: REMOVE_POSTS_BULK_IMPORT_ITEM,
    payload: itemId,
  });
};

export const getTwitterMentions = (query) => (dispatch) => {
  return new Promise((resolve, reject) => {
    const url = Format(PLANNER_TWITTER_MENTIONS_FETCH_ENDPOINT, query);
    const getItemText = (item) => `@${item.screen_name}`;

    fetchJsonp(url)
      .then(async (response) => {
        const data = await response.json();
        const items = data?.users?.map((item) => ({
          source: PostEditorTabsTypes.TWITTER,
          id: item.screen_name,
          name: item.name,
          text: getItemText(item),
          image: item.profile_image_url_https,
        }));

        const res = {
          ...response,
          data: {
            status: 1,
            items,
          },
        };

        handlePlannerResponse(res, dispatch);
        resolve(res);
      })
      .catch((error) => {
        handlePlannerResponse(error.response, dispatch);
        dispatch(handleHTTPError(error));
        reject(error);
      });
  });
};

export const getLinkedInMentions = (query) => (dispatch) => {
  return new Promise((resolve, reject) => {
    const url = Format(PLANNER_MENTIONS_FETCH_ENDPOINT, "linkedin", query);
    const getItemText = (item) => `@${item.name}`;

    axios
      .get(url)
      .then((response) => {
        const items = response?.data?.items?.map((item) => ({
          source: PostEditorTabsTypes.LINKEDIN,
          id: item.id,
          name: item.name,
          text: getItemText(item),
          image: item.image,
        }));

        const res = {
          ...response,
          data: {
            ...response.data,
            items,
          },
        };

        handlePlannerResponse(res, dispatch);
        resolve(res);
      })
      .catch((error) => {
        handlePlannerResponse(error.response, dispatch);
        dispatch(handleHTTPError(error));
        reject(error);
      });
  });
};

export const getFacebookMentions = (query) => (dispatch) => {
  return new Promise((resolve, reject) => {
    const url = Format(PLANNER_MENTIONS_FETCH_ENDPOINT, "facebook", query);
    const getItemText = (item) => `@${item.name}`;

    axios
      .get(url)
      .then((response) => {
        const items = response?.data?.items?.map((item) => ({
          source: PostEditorTabsTypes.FACEBOOK,
          id: item.id,
          name: item.name,
          text: getItemText(item),
          image: item.image,
        }));

        const res = {
          ...response,
          data: {
            ...response.data,
            items,
          },
        };

        handlePlannerResponse(res, dispatch);
        resolve(res);
      })
      .catch((error) => {
        handlePlannerResponse(error.response, dispatch);
        dispatch(handleHTTPError(error));
        reject(error);
      });
  });
};

export const savePost = (data) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .post(PLANNER_SAVE_POST_ENDPOINT, data)
      .then((res) => {
        handlePlannerResponse(res, dispatch);
        resolve(res);
      })
      .catch((error) => {
        handlePlannerResponse(error.response, dispatch);
        dispatch(handleHTTPError(error));
        reject(error);
      });
  });
};

export const addSchedulePost = (data, options) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .post(PLANNER_ADD_SCHEDULE_POST_ENDPOINT, data)
      .then((res) => {
        if (!options?.hideToast) {
          handlePlannerResponse(res, dispatch);
        }
        resolve(res);
      })
      .catch((error) => {
        if (!options?.hideToast) {
          handlePlannerResponse(error.response, dispatch);
        }
        dispatch(handleHTTPError(error));
        reject(error);
      });
  });
};

export const addPublishNowPost = (data) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .post(PLANNER_ADD_ASYNC_PUBLISH_NOW_POST_ENDPOINT, data)
      .then((res) => {
        if (res.data.status === 1) {
          const PENDING_STATUS = 100;

          const timer = setInterval(async () => {
            try {
              const statusResponse = await axios.get(
                PLANNER_ADD_ASYNC_STATUS_POST_ENDPOINT,
                {
                  params: {
                    ids: res.data?.results || [],
                  },
                }
              );

              if (statusResponse.data.status !== PENDING_STATUS) {
                clearInterval(timer);
                handlePlannerResponse(statusResponse, dispatch);
                resolve(statusResponse);
              }
            } catch (error) {
              handlePlannerResponse(error.response, dispatch);
              reject(error);
            }
          }, 5000);
        } else {
          handlePlannerResponse(res, dispatch);
          resolve(res);
        }
      })
      .catch((error) => {
        handlePlannerResponse(error.response, dispatch);
        dispatch(handleHTTPError(error));
        reject(error);
      });
  });
};

export const addBufferPost = (data, options) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .post(PLANNER_ADD_BUFFER_POST_ENDPOINT, data)
      .then((res) => {
        if (!options?.hideToast) {
          handlePlannerResponse(res, dispatch);
        }
        resolve(res);
      })
      .catch((error) => {
        if (!options?.hideToast) {
          handlePlannerResponse(error.response, dispatch);
        }
        dispatch(handleHTTPError(error));
        reject(error);
      });
  });
};

export const saveDraft = (data) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .post(PLANNER_SAVE_DRAFT_ENDPOINT, data)
      .then((res) => {
        handlePlannerResponse(res, dispatch);
        resolve(res);
      })
      .catch((error) => {
        handlePlannerResponse(error.response, dispatch);
        dispatch(handleHTTPError(error));
        reject(error);
      });
  });
};

export const addDraft = (data) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .post(PLANNER_ADD_DRAFT_ENDPOINT, data)
      .then((res) => {
        handlePlannerResponse(res, dispatch);
        resolve(res);
      })
      .catch((error) => {
        handlePlannerResponse(error.response, dispatch);
        dispatch(handleHTTPError(error));
        reject(error);
      });
  });
};

export const addForApprovalDraft = (data) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .post(PLANNER_ADD_FOR_APPROVAL_DRAFT_ENDPOINT, data)
      .then((res) => {
        handlePlannerResponse(res, dispatch);
        resolve(res);
      })
      .catch((error) => {
        handlePlannerResponse(error.response, dispatch);
        dispatch(handleHTTPError(error));
        reject(error);
      });
  });
};

export const getSnippets = () => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .get(PLANNER_GET_SNIPPETS_ENDPOINT)
      .then((res) => {
        if (res.data.status !== 1) {
          handlePlannerResponse(res, dispatch);
        }

        resolve(res);
      })
      .catch((error) => {
        handlePlannerResponse(error.response, dispatch);
        dispatch(handleHTTPError(error));
        reject(error);
      });
  });
};

export const addSnippet =
  ({ text, title }) =>
  (dispatch) => {
    return new Promise((resolve, reject) => {
      axios
        .post(PLANNER_ADD_SNIPPET_ENDPOINT, {
          text,
          title,
        })
        .then((res) => {
          handlePlannerResponse(res, dispatch);
          resolve(res);
        })
        .catch((error) => {
          handlePlannerResponse(error.response, dispatch);
          dispatch(handleHTTPError(error));
          reject(error);
        });
    });
  };

export const removeSnippet = (id) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .post(PLANNER_REMOVE_SNIPPET_ENDPOINT, {
        id,
      })
      .then((res) => {
        handlePlannerResponse(res, dispatch);
        resolve(res);
      })
      .catch((error) => {
        handlePlannerResponse(error.response, dispatch);
        dispatch(handleHTTPError(error));
        reject(error);
      });
  });
};

export const editSnippet =
  ({ id, text, title }) =>
  (dispatch) => {
    return new Promise((resolve, reject) => {
      axios
        .post(PLANNER_EDIT_SNIPPET_ENDPOINT, {
          id,
          text,
          title,
        })
        .then((res) => {
          handlePlannerResponse(res, dispatch);
          resolve(res);
        })
        .catch((error) => {
          handlePlannerResponse(error.response, dispatch);
          dispatch(handleHTTPError(error));
          reject(error);
        });
    });
  };

export const markSnippetAsUsed = (id) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .post(PLANNER_USED_SNIPPET_ENDPOINT, {
        id,
      })
      .then((res) => {
        handlePlannerResponse(res, dispatch);
        resolve(res);
      })
      .catch((error) => {
        handlePlannerResponse(error.response, dispatch);
        dispatch(handleHTTPError(error));
        reject(error);
      });
  });
};

export const getAvailablePostsQuota = () => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .get(PLANNER_GET_AVAILABLE_POSTS_QUOTA)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const createMultipartUpload = (file) => {
  return new Promise((resolve, reject) => {
    axios
      .post(PLANNER_MULTIPART_UPLOAD_ENDPOINT, {
        action: "create",
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      })
      .then((response) => {
        resolve({
          uploadId: response.data.uploadId,
          key: response.data.key,
          dir: response.data.dir,
          fileName: response.data.fileName,
        });
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const listMultipartUploadParts = (file, { uploadId, key }) => {
  return new Promise((resolve, reject) => {
    axios
      .post(PLANNER_MULTIPART_UPLOAD_ENDPOINT, {
        action: "list-parts",
        fileName: file.name,
        fileType: file.type,
        uploadId,
        key,
      })
      .then((response) => {
        resolve(response.data?.parts || []);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const prepareMultipartUploadParts = (file, partData) => {
  return new Promise((resolve, reject) => {
    axios
      .post(PLANNER_MULTIPART_UPLOAD_ENDPOINT, {
        action: "sign-parts",
        fileName: file.name,
        fileType: file.type,
        uploadId: partData.uploadId,
        partNumbers: partData.partNumbers,
        key: partData.key,
      })
      .then((response) => {
        resolve({
          presignedUrls: response.data.presignedUrls,
        });
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const abortMultipartUpload = (file, { uploadId, key }) => {
  return new Promise((resolve, reject) => {
    axios
      .post(PLANNER_MULTIPART_UPLOAD_ENDPOINT, {
        action: "abort",
        fileName: file?.name,
        fileType: file?.type,
        uploadId,
        key,
      })
      .then(() => {
        resolve({});
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const completeMultipartUpload = (
  file,
  { uploadId, key, parts },
  uppy
) => {
  return new Promise((resolve, reject) => {
    axios
      .post(PLANNER_MULTIPART_UPLOAD_ENDPOINT, {
        action: "complete",
        fileName: file.name,
        fileType: file.type,
        uploadId,
        key,
        parts,
      })
      .then(async (response) => {
        const { bucket, region, ...uploadResponse } = response.data;
        const getAssetType = (type) => {
          const mimeType = type?.split("/")?.[0];

          if (type === "image/gif") {
            return 4;
          } else if (mimeType === "video") {
            return 2;
          } else if (mimeType === "image") {
            return 0;
          }

          return null;
        };

        try {
          const duration = await getMediaDurationFromFile(file.data);
          const fileResolution = await getHeightAndWidth(file.data);
          const assetResponse = await axios.post(ASSETS_ENDPOINT, {
            asset_type: getAssetType(file.type),
            region,
            bucket_name: bucket,
            asset_key: key,
            payload: {
              duration,
            },
            thumbnail: response.data.url,
            thumbnail_width: fileResolution.width,
            thumbnail_height: fileResolution.height,
          });

          if (assetResponse?.data?.id) {
            const timer = setInterval(async () => {
              try {
                const taskResponse = await axios.get(
                  Format(CHECK_TASK_STATAUS, assetResponse.data.id)
                );

                if (uppy && !uppy.getFile(file.id)) {
                  reject(new Error("File upload aborted."));
                  clearInterval(timer);
                } else if (taskResponse?.data?.status === "FAILURE") {
                  reject(new Error("File upload failed."));
                  clearInterval(timer);
                } else if (taskResponse?.data?.status === "SUCCESS") {
                  const assetDataResponse = await axios.get(
                    `${ASSETS_ENDPOINT}/${assetResponse.data.id}`
                  );

                  if (assetDataResponse?.data?.thumbnail_cover_image) {
                    const data = {
                      ...uploadResponse,
                      thumbUrl: assetDataResponse.data.thumbnail_cover_image,
                    };

                    if (file.type?.split("/")?.[0] === "video") {
                      data.thumbTime = 0;
                    }

                    resolve(data);
                  } else {
                    reject(
                      new Error("Retrieving the uploaded file data failed.")
                    );
                  }

                  clearInterval(timer);
                }
              } catch (error) {
                reject(error);
              }
            }, 5000);
          } else {
            reject(new Error("Unknown error occurred."));
          }
        } catch (error) {
          reject(error);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const fetchPlannerWorkflowType = () => {
  // currently planner workflow type is only returned from social accounts endpoint,
  // which also sets up account in planner's db if it's called for the first time.
  return new Promise((resolve, reject) => {
    axios
      .get(SOCIAL_ACCOUNTS_GET_ADDED)
      .then((res) => {
        resolve(res.data?.workflowType);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const updateWorkflowType = (type) => {
  return new Promise((resolve, reject) => {
    axios
      .post(PLANNER_UPDATE_WORKFLOW_TYPE_ENDPOINT, { type })
      .then((res) => {
        handlePlannerResponse(res);
        if (res.data.status === 1) {
          resolve(res);
        } else {
          reject(res?.results[0]?.message);
        }
      })
      .catch((error) => {
        handlePlannerResponse(error.response);
        reject(error);
      });
  });
};

export const setWorkflowType = (payload) => (dispatch) => {
  dispatch({
    type: SET_WORKFLOW_TYPE,
    payload,
  });
};

export const setDraftsView = (payload) => (dispatch) => {
  dispatch({
    type: SET_DRAFTS_VIEW,
    payload,
  });
};

export const refreshDraftsView = () => (dispatch) => {
  dispatch({
    type: REFRESH_DRAFTS_VIEW,
  });
};

export const setDraftsList = (payload) => (dispatch) => {
  dispatch({
    type: SET_DRAFTS_LIST,
    payload,
  });
};

export const fetchDrafts =
  ({ accountsIds, page, search, status, timezone, perPage, userIds }) =>
  (dispatch) => {
    const params = {
      accountsIds,
      page,
      perPage,
      search,
      tz: timezone,
      status,
      uid: userIds,
    };

    return new Promise((resolve, reject) => {
      axios
        .get(PLANNER_GET_DRAFTS_ENDPOINT, { params })
        .then((res) => {
          if (res.data.status === 1) {
            dispatch({
              type: SET_DRAFTS_DATA,
              payload: {
                items: res.data.posts.list,
                params: {
                  ...params,
                  page: res.data.posts.current_page,
                },
                pages_count: res.data.posts.pages_count,
                current_page: res.data.posts.current_page,
                hasMore:
                  res.data.posts.current_page < res.data.posts.pages_count,
                append: res.data.posts.current_page > 1,
              },
            });
          } else {
            handlePlannerResponse(res, dispatch);
          }

          resolve(res);
        })
        .catch((error) => {
          handlePlannerResponse(error.response, dispatch);
          dispatch(handleHTTPError(error));
          reject(error);
        });
    });
  };

export const fetchDraft = (draftId) => {
  const url = Format(PLANNER_GET_DRAFT_ENDPOINT, draftId);
  return new Promise((resolve, reject) => {
    axios
      .get(url)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const fetchDraftHistory = (draftId) => {
  const url = Format(PLANNER_GET_DRAFT_HISTORY_ENDPOINT, draftId);
  return new Promise((resolve, reject) => {
    axios
      .get(url)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const updateDraftStatus =
  ({ draftIds, status }) =>
  (dispatch) => {
    return new Promise((resolve, reject) => {
      axios
        .post(PLANNER_SET_DRAFT_STATUS, {
          id: status,
          postIds: draftIds,
        })
        .then((res) => {
          handlePlannerResponse(res, dispatch);
          dispatch(refreshDraftsView());
          resolve(res);
        })
        .catch((error) => {
          handlePlannerResponse(error.response, dispatch);
          reject(error);
        });
    });
  };

export const deleteDraft = (draftIds) => (dispatch) => {
  const ids = Array.isArray(draftIds) ? draftIds : [draftIds];

  return new Promise((resolve, reject) => {
    axios
      .post(PLANNER_DELETE_DRAFT_ENDPOINT, {
        ids,
      })
      .then((res) => {
        dispatch({
          type: DELETE_DRAFT,
          payload: ids
        });

        handlePlannerResponse(res, dispatch);
        resolve(res);
      })
      .catch((error) => {
        handlePlannerResponse(error.response, dispatch);
        dispatch(handleHTTPError(error));
        reject(error);
      });
  });
};

export const fetchApprovalPackages = () =>
  new Promise((resolve, reject) => {
    axios
      .get(PLANNER_GET_APPROVAL_PACKAGES)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        handlePlannerResponse(error.response);
        reject(error);
      });
  });

  export const fetchApprovalPackage = (id) =>
  new Promise((resolve, reject) => {
    axios
      .get(Format(PLANNER_GET_APPROVAL_PACKAGE, id))
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        handlePlannerResponse(error.response);
        reject(error);
      });
  });

export const createApprovalPackage = ({
  description,
  draftIds,
  name
}) => new Promise((resolve, reject) => {
  axios
    .post(PLANNER_CREATE_APPROVAL_PACKAGE, {
      description,
      draftsIds: draftIds,
      title: name
    })
    .then((res) => {
      handlePlannerResponse(res);
      resolve(res);
    })
    .catch((error) => {
      handlePlannerResponse(error.response);
      reject(error);
    });
});

export const updateApprovalPackage = ({
  packageId,
  draftIds,
}) => new Promise((resolve, reject) => {
  axios
    .post(PLANNER_UPDATE_APPROVAL_PACKAGE, {
      draftsIds: draftIds,
      packageId,
    })
    .then((res) => {
      handlePlannerResponse(res);
      resolve(res);
    })
    .catch((error) => {
      handlePlannerResponse(error.response);
      reject(error);
    });
});

export const approveDraftInPackage = ({
  draftId,
  packageId,
}) => new Promise((resolve, reject) => {
  axios
    .post(PLANNER_APPROVAL_PACKAGE_APPROVE, {
      draftId,
      packageId,
    })
    .then((res) => {
      handlePlannerResponse(res);
      resolve(res);
    })
    .catch((error) => {
      handlePlannerResponse(error.response);
      reject(error);
    });
});

export const rejectDraftInPackage = ({
  draftId,
  packageId,
}) => new Promise((resolve, reject) => {
  axios
    .post(PLANNER_APPROVAL_PACKAGE_REJECT, {
      draftId,
      packageId,
    })
    .then((res) => {
      handlePlannerResponse(res);
      resolve(res);
    })
    .catch((error) => {
      handlePlannerResponse(error.response);
      reject(error);
    });
});

export const generateReportFile = ({
  html,
  width,
  height,
  fileType,
  format,
  margin,
}) => (dispatch) => new Promise((resolve, reject) => {
  axios({
    method: 'post',
    url: PLANNER_GENERATE_REPORT_ENDPOINT,
    data: {
      html,
      width,
      height,
      fileType,
      format,
      margin
    }
  })
    .then((res) => {
      resolve(res);
    })
    .catch((error) => {
      dispatch(handleHTTPError(error));
      reject(error);
    });
});
